import React, { useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode"; // Import jwt-decode
import ReactMarkdown from "react-markdown";
import { CircleQuestionMark, LogOut } from "lucide-react";
import { Warp } from "@paper-design/shaders-react";

import "./App.css";
import CrossAppCommunicator from "./crossAppCommunicator";
import NotFound from "./NotFound";
import IPBlockedPage from "./IPBlockedPage";
import { checkIpAccess } from "./ipAccessService";
import config from "./config.js";
import { getAppIcon } from "./appIcons";
import { fetchApps, fetchHelpFile } from "./services/appService";
import { resolveSidebarWidthPx } from "./sidebarWidth";

const ENV = process.env.REACT_APP_ENV || "development";

// In the dockerised local stack the BFF bypasses Google OAuth entirely
// (see integral-stack/RULES.md → "Jantro stack"). The FE has to
// match: skip the sign-in screen and ship a stub token so `appService`
// still attaches an Authorization header (the BFF discards its value).
// Defense-in-depth: the env check (`local-container`) is the *intended*
// gate, but we also pin to a localhost hostname so a misconfigured
// non-local deploy that happens to inherit `local-container` from a
// .env override can't silently bypass real auth in production.
// `typeof window !== "undefined"` keeps this safe for any SSR / build-
// time evaluation (returns false, no bypass).
const IS_LOCAL_CONTAINER =
  ENV === "local-container" &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
const LOCAL_CONTAINER_TOKEN = "local-container-stub-token";
const LOCAL_CONTAINER_EMAIL = "local@integral.de";

const LAST_APP_STORAGE_KEY = "lastAppSlug";
// Grace period for an embedded app to complete the cross-app handshake before
// the parent treats it as unavailable. Generous to absorb cold starts; the
// healthy child handshake normally lands within ~1–2s of the iframe loading.
const APP_HANDSHAKE_TIMEOUT_MS = 10000;
const ENV_SUFFIXES = ["-integral-audit", "-stage", "-prod", "-dev", "-test"];

// Strips env suffixes so the URL slug survives promotion across envs.
// Mirrors getBaseAppName in functions/routes/apps.js.
function getBaseAppName(name) {
  for (const suffix of ENV_SUFFIXES) {
    if (name && name.endsWith(suffix)) return name.slice(0, -suffix.length);
  }
  return name || "";
}

function pathForSlug(slug) {
  return "/" + encodeURIComponent(slug);
}

function pathForSlugAndChildRoute(slug, childRoute) {
  const basePath = pathForSlug(slug);
  return childRoute && childRoute !== "/" ? `${basePath}${childRoute}` : basePath;
}

function getSlugFromPath(pathname) {
  if (!pathname || pathname === "/") return null;
  const [slug] = pathname.replace(/^\/+/, "").split("/");
  if (!slug) return null;
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
}

function getChildRouteFromPath(pathname, slug) {
  if (!pathname || !slug) return "/";
  const [, ...parts] = pathname.replace(/^\/+/, "").split("/");
  return normalizeChildRoute(parts.length ? `/${parts.join("/")}` : "/");
}

function normalizeChildRoute(route) {
  if (!route || route === "/") return "/";
  if (/^\/tasks\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(route)) {
    return route;
  }
  return "/";
}

function supportsChildRouteSync(app) {
  return getBaseAppName(app?.name) === "jantro-007";
}

function findAppBySlug(apps, slug) {
  if (!slug) return null;
  return apps.find((a) => getBaseAppName(a.name) === slug) || null;
}

const resolvedSidebarWidthPx = resolveSidebarWidthPx({
  configWidthPx: config?.sidebarWidthPx,
  envWidthOverride: process.env.REACT_APP_SIDEBAR_WIDTH,
});

const IntegralMark = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M111.613 0H8.3572C3.72725 0 0 3.72726 0 8.3572V111.643C0 116.244 3.72725 120 8.3572 120H111.643C116.244 120 120 116.273 120 111.643V8.3572C120 3.75637 116.273 0 111.643 0H111.613ZM70.7015 99.0251L55.5012 90.2891C51.6283 88.0761 49.2405 83.9412 49.2405 79.486C49.2405 75.0308 51.803 70.6336 55.8795 68.479L70.6723 60.646L70.7015 99.0251ZM64.0621 52.4626L49.2697 60.2957V21.9168L64.47 30.6525C68.3425 32.8656 70.7303 37.0004 70.7303 41.4556C70.7303 45.9108 68.1678 50.308 64.0913 52.4626H64.0621Z"
      fill="currentColor"
    />
  </svg>
);

const JantroBrand = ({ showStageTab }) => (
  <div className="jantro-brand">
    <div className="jantro-brand__mark-wrap">
      <IntegralMark className="jantro-brand__mark" />
      {showStageTab && (
        <span className="jantro-brand__stage-tab label">
          <span className="jantro-brand__stage-dot" aria-hidden="true" />
          {stageTabLabel}
        </span>
      )}
    </div>
    <span className="jantro-brand__wordmark label">Jantro</span>
  </div>
);

const showStageTab = !ENV.startsWith("prod");
const stageTabLabel = "DEV";

// Static brand-coloured panel shown when the WebGL shader can't run
// (WebGL2 unavailable) or the user prefers reduced motion.
const WarpFallback = () => <div className="login-shell__warp-fallback" />;

// Defence-in-depth: degrade to the static fallback if the shader throws during
// render. (The WebGL2 check below is the primary guard; the library's own
// context failure surfaces async, which a boundary cannot catch.)
class WarpBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("Login shader failed; using static fallback:", error);
  }

  render() {
    return this.state.failed ? <WarpFallback /> : this.props.children;
  }
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// The shader requires a WebGL2 context. When it's missing the library throws
// from inside an async effect (an unhandled rejection an error boundary can't
// catch), leaving a blank panel — so detect support up front and fall back.
const supportsWebGl2 = () => {
  if (typeof document === "undefined") return false;
  try {
    return !!document.createElement("canvas").getContext("webgl2");
  } catch {
    return false;
  }
};

// Animated brand panel for the login screen, matching the client-canvas v2 login.
const LoginWarp = () => {
  if (prefersReducedMotion() || !supportsWebGl2()) {
    return <WarpFallback />;
  }
  return (
    <WarpBoundary>
      <Warp
        swirl={0.9}
        swirlIterations={6}
        shape="checks"
        distortion={0.27}
        shapeScale={0.25}
        speed={0.5}
        scale={2.87}
        colors={["#3B1515", "#954751", "#FFC085"]}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
    </WarpBoundary>
  );
};

function GoogleSignIn({ onSuccess }) {
  const buttonDivRef = useRef(null);

  useEffect(() => {
    function initializeGSI() {
      /* global google */
      google.accounts.id.initialize({
        client_id: config.googleAuthClientId,
        callback: (response) => {
          console.log("Google ID Token:", response.credential);
          onSuccess(response.credential);
        },
      });
      if (buttonDivRef.current) {
        google.accounts.id.renderButton(buttonDivRef.current, {
          theme: "outline",
          size: "large",
        });
      }
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGSI();
    } else {
      window.onGoogleScriptLoad = initializeGSI;
    }
  }, [onSuccess]);

  return <div ref={buttonDivRef} />;
}

function App() {
  const iframeRef = useRef(null);
  const communicatorRef = useRef(null);
  const skipNextIframeRouteSyncRef = useRef(false);
  // Watchdog for embedded apps: a healthy child completes the cross-app
  // handshake (getToken/getApiBaseUrl) shortly after load. A dead/misdeployed
  // target (e.g. a retired Firebase site serving the stock 404.html) never
  // does — we can't read its cross-origin status, so we infer "unavailable"
  // from the absence of that handshake within APP_HANDSHAKE_TIMEOUT_MS.
  const handshakeReceivedRef = useRef(false);
  const watchdogTimerRef = useRef(null);
  const [token, setToken] = useState(() =>
    IS_LOCAL_CONTAINER
      ? localStorage.getItem("token") || LOCAL_CONTAINER_TOKEN
      : localStorage.getItem("token")
  );
  const [userEmail, setUserEmail] = useState(() =>
    IS_LOCAL_CONTAINER
      ? localStorage.getItem("userEmail") || LOCAL_CONTAINER_EMAIL
      : localStorage.getItem("userEmail") || ""
  );
  const [showGoogleConnect, setShowGoogleConnect] = useState(
    IS_LOCAL_CONTAINER ? false : !localStorage.getItem("token")
  );
  const [selectedApp, setSelectedApp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [appUnavailable, setAppUnavailable] = useState(false);
  const [projects, setProjects] = useState([]);
  const [helpVisible, setHelpVisible] = useState(false);
  const [helpMarkdown, setHelpMarkdown] = useState("");
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  const [iframeRoute, setIframeRoute] = useState("/");
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  // Soft IP gate (Layer 1). null = still checking, true = allowed, false = blocked.
  // Fails open: ipAccessService only returns blocked when the BFF explicitly says so.
  const [ipAllowed, setIpAllowed] = useState(IS_LOCAL_CONTAINER ? true : null);

  useEffect(() => {
    if (IS_LOCAL_CONTAINER) {
      return;
    }
    let cancelled = false;
    checkIpAccess().then(({ allowed }) => {
      if (cancelled) return;
      setIpAllowed(allowed);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const iframeSrc = useMemo(() => {
    if (!selectedApp) return null;

    const baseUrl = selectedApp.url.replace(/\/+$/, "");
    const route = supportsChildRouteSync(selectedApp) ? iframeRoute : "/";
    const routePath = route === "/" ? "" : route;
    return `${baseUrl}${routePath}?v=${Date.now()}`;
  }, [selectedApp, iframeRoute]);
  const appStyle = {
    "--jantro-sidebar-width-expanded": `${resolvedSidebarWidthPx}px`,
  };

  const handleLogout = () => {
    setToken(null);
    setUserEmail(""); // Clear user email on logout
    setSelectedApp(null); // Clear selected app on logout
    localStorage.removeItem("token");
    localStorage.removeItem("userEmail"); // Remove user email from localStorage
    localStorage.removeItem(LAST_APP_STORAGE_KEY);
    if (window.location.pathname !== "/") {
      window.history.pushState(null, "", "/");
      setPathname("/");
    }
  };

  const selectApp = (app) => {
    setHelpVisible(false);
    setNotFound(false);
    setAppUnavailable(false);
    setSelectedApp(app);
    const slug = getBaseAppName(app?.name);
    if (slug) {
      localStorage.setItem(LAST_APP_STORAGE_KEY, slug);
      setIframeRoute("/");
      const nextPath = pathForSlugAndChildRoute(slug, "/");
      if (window.location.pathname !== nextPath) {
        window.history.pushState(null, "", nextPath);
        setPathname(nextPath);
      }
    }
  };

  // Recovery action from the 404 view: clear the bad path and fall back to the
  // app picker / last-used app via the routing effect below.
  const handleGoHome = () => {
    setNotFound(false);
    setAppUnavailable(false);
    if (window.location.pathname !== "/") {
      window.history.pushState(null, "", "/");
    }
    setPathname("/");
  };

  const toggleHelpOverlay = () => {
    fetchHelpFile(token).then((data) => {
      setHelpMarkdown(data || "No help available.");
    });
    setHelpVisible((prev) => !prev);
  };

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!token || projects.length === 0) return;
    const urlSlug = getSlugFromPath(pathname);
    const fallbackSlug = urlSlug ? null : localStorage.getItem(LAST_APP_STORAGE_KEY);
    const slug = urlSlug || fallbackSlug;
    const match = findAppBySlug(projects, slug);
    const childRoute = urlSlug ? getChildRouteFromPath(pathname, urlSlug) : "/";
    if (match && match.id !== selectedApp?.id) {
      setNotFound(false);
      setIframeRoute(supportsChildRouteSync(match) ? childRoute : "/");
      setSelectedApp(match);
    }
    if (match && match.id === selectedApp?.id && supportsChildRouteSync(match)) {
      if (skipNextIframeRouteSyncRef.current) {
        skipNextIframeRouteSyncRef.current = false;
      } else if (childRoute !== iframeRoute) {
        setIframeRoute(childRoute);
      }
    }
    // Normalise URL when we resolved via localStorage fallback.
    if (match && !urlSlug) {
      const nextPath = pathForSlugAndChildRoute(getBaseAppName(match.name), "/");
      window.history.replaceState(null, "", nextPath);
      setPathname(nextPath);
    }
    // URL pointed at an unknown/forbidden app: show the 404 view (keeping the
    // bad URL in the address bar) instead of silently bouncing to the picker.
    if (!match && urlSlug) {
      setNotFound(true);
    }
    // Auto-select first app only when the URL had no slug at all (home / a
    // localStorage fallback that didn't resolve) — never override a 404.
    if (!match && !urlSlug && !selectedApp && projects.length > 0) {
      selectApp(projects[0]);
    }
  }, [token, projects, pathname, selectedApp, iframeRoute]);

  useEffect(() => {
    const handleAuthError = () => {
      handleLogout();
      // Optionally, redirect to login page or home if not already handled by handleLogout
    };

    const loadApps = async () => {
      if (token) {
        const fetchedApps = await fetchApps(token, handleAuthError);
        // Hide stale whitelist entries. jantro-utm-admin-gateway is an orphan
        // from the pre-rename deploy (now jantro-utm-admin); its hosting site
        // serves a 404 and the Firestore doc lingers.
        const visibleApps = fetchedApps.filter(
          (a) => getBaseAppName(a.name) !== "jantro-utm-admin-gateway"
        );
        setProjects(visibleApps); // Still using 'projects' state variable, but it now holds 'apps'
      }
    };

    if (token) {
      loadApps();

      // In local-container mode the token is a static stub (not a JWT),
      // so jwtDecode would throw and handleLogout would wipe everything.
      // Skip the decode and use the literal local user instead.
      if (IS_LOCAL_CONTAINER) {
        setUserEmail(LOCAL_CONTAINER_EMAIL);
        localStorage.setItem("userEmail", LOCAL_CONTAINER_EMAIL);
      } else {
        try {
          const decodedToken = jwtDecode(token);
          setUserEmail(decodedToken.email);
          localStorage.setItem("userEmail", decodedToken.email);
        } catch (error) {
          console.error("Error decoding token:", error);
          handleLogout(); // Log out if token is invalid
        }
      }

      // If token exists, hide Google Connect
      setShowGoogleConnect(false);
    } else {
      // If no token, show Google Connect (skipped entirely in local mode).
      setShowGoogleConnect(!IS_LOCAL_CONTAINER);
    }

    if (token && selectedApp) {
      // Initialize communicator for parent app, targeting child app's origin
      communicatorRef.current = new CrossAppCommunicator("Parent App", selectedApp.url);

      // Register handlers for messages from child app
      communicatorRef.current.on("childEvent", (payload) => {
        console.log("Parent App received childEvent:", payload);

        // Any message proves the embedded app booted: cancel the unavailable
        // watchdog (see APP_HANDSHAKE_TIMEOUT_MS) and clear a stale fallback.
        handshakeReceivedRef.current = true;
        if (watchdogTimerRef.current) {
          clearTimeout(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
        setAppUnavailable(false);

        if (payload.command && payload.command === "getToken") {
          // Send real token if available, else show Google Connect
          if (token && iframeRef.current?.contentWindow) {
            communicatorRef.current.sendMessage(iframeRef.current.contentWindow, "parentEvent", {
              command: "setToken",
              token,
              email: userEmail || null,
            });
          }
          return;
        }
        if (payload.command && payload.command === "getApiBaseUrl") {
          // Send real token if available, else show Google Connect
          if (iframeRef.current?.contentWindow) {
            communicatorRef.current.sendMessage(iframeRef.current.contentWindow, "parentEvent", {
              command: "setApiBaseUrl",
              apiBaseUrl: config.apiBaseUrl,
            });
          }

          return;
        }
        if (payload.command && payload.command === "setChildRoute") {
          if (!selectedApp || payload.app !== getBaseAppName(selectedApp.name)) return;

          const childRoute = normalizeChildRoute(payload.route);
          const slug = getBaseAppName(selectedApp.name);
          const nextPath = pathForSlugAndChildRoute(slug, childRoute);
          if (window.location.pathname !== nextPath) {
            skipNextIframeRouteSyncRef.current = true;
            window.history.pushState(null, "", nextPath);
            setPathname(nextPath);
          }

          return;
        }
      });
    } else {
      setShowGoogleConnect(true);
    }

    // Clean up on unmount
    return () => {
      if (communicatorRef.current) {
        communicatorRef.current.destroy();
      }
    };
  }, [token, userEmail, selectedApp, helpMarkdown, iframeRoute]);

  // Unavailable-app watchdog. Restarts whenever a new iframe URL is loaded
  // (app switch or child-route deep link); if the embedded app hasn't said
  // anything by the timeout, surface the in-app "app unavailable" view instead
  // of leaving the user staring at the child's raw 404.
  useEffect(() => {
    if (!token || !selectedApp || !iframeSrc || notFound) return;

    handshakeReceivedRef.current = false;
    setAppUnavailable(false);
    if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    watchdogTimerRef.current = setTimeout(() => {
      if (!handshakeReceivedRef.current) {
        console.warn(
          `No handshake from "${selectedApp.name}" within ${APP_HANDSHAKE_TIMEOUT_MS}ms — marking app unavailable.`
        );
        setAppUnavailable(true);
      }
    }, APP_HANDSHAKE_TIMEOUT_MS);

    return () => {
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedApp?.id, iframeSrc, notFound]);

  if (ipAllowed === false) {
    return <IPBlockedPage />;
  }

  if (ipAllowed === null) {
    return (
      <div className="App" style={appStyle}>
        <header className="loading-container">
          <div className="spinner"></div>
        </header>
      </div>
    );
  }

  if (token) {
    return (
      <div className="App" style={appStyle}>
        <aside
          className={`sidebar ${sidebarExpanded ? "expanded" : ""}`}
          onMouseEnter={() => setSidebarExpanded(true)}
          onMouseLeave={() => setSidebarExpanded(false)}
          onFocus={() => setSidebarExpanded(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setSidebarExpanded(false);
            }
          }}
        >
          <header>
            <JantroBrand showStageTab={showStageTab} />
          </header>
          <div className="app-list">
            <ul>
              {projects.map((project) => {
                const Icon = getAppIcon(getBaseAppName(project.name));
                return (
                  <li key={project.id} className={project.id === selectedApp?.id ? "active" : ""}>
                    <button
                      className="nav-item"
                      onClick={() => {
                        selectApp(project);
                      }}
                      title={project.title}
                    >
                      <span className="nav-item__inner">
                        <Icon className="app-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
                        <span className="label">{project.title}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <footer>
            <div className="footer-actions">
              <button
                onClick={toggleHelpOverlay}
                className={`nav-item help-button ${helpVisible ? "active" : ""}`}
                title="Help"
              >
                <span className="nav-item__inner">
                  <CircleQuestionMark className="app-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
                  <span className="label">Help</span>
                </span>
              </button>
              <button onClick={handleLogout} className="nav-item logout-button" title="Logout">
                <span className="nav-item__inner">
                  <LogOut className="app-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
                  <span className="label">Logout</span>
                </span>
              </button>
            </div>
            <div className="user-container">
              <p className="label">{userEmail}</p>
              <small className="label">
                {process.env.REACT_APP_ENV} • {process.env.REACT_APP_BUILD_REFERENCE}
              </small>
            </div>
          </footer>
        </aside>
        {helpVisible && (
          <div className="help-container">
            <h2>Help</h2>
            <div className="md-preview-container">
              <ReactMarkdown>{helpMarkdown}</ReactMarkdown>
            </div>
          </div>
        )}

        <div className="main-content">
          {notFound ? (
            <NotFound pathname={pathname} onHome={handleGoHome} />
          ) : selectedApp ? (
            <div className="iframe-container">
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                title={selectedApp.name}
                allow="clipboard-write"
              ></iframe>
              {appUnavailable && (
                <div className="app-unavailable-overlay">
                  <NotFound
                    pathname={pathname}
                    onHome={handleGoHome}
                    eyebrow="Error — app unavailable"
                    title="This app couldn’t be loaded."
                    body="The app didn’t respond — it may be offline or still deploying. Return to your workspace overview and try another app."
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="iframe-placeholder">Select an app to load it in the iframe.</div>
          )}
        </div>
      </div>
    );
  }

  if (!token && showGoogleConnect) {
    return (
      <div className="login-shell">
        <div className="login-shell__warp" aria-hidden="true">
          <LoginWarp />
        </div>
        <div className="login-shell__form">
          <div className="login-shell__col">
            <div className="jantro-brand jantro-brand--hero">
              <div className="jantro-brand__mark-wrap">
                <IntegralMark className="jantro-brand__mark" />
                {showStageTab && (
                  <span className="jantro-brand__stage-tab">
                    <span className="jantro-brand__stage-dot" aria-hidden="true" />
                    {stageTabLabel}
                  </span>
                )}
              </div>
              <span className="jantro-brand__wordmark">Jantro</span>
            </div>
            <p className="login-shell__prompt">
              Please connect to Google to proceed.
            </p>
            <GoogleSignIn
              onSuccess={(idToken) => {
                setToken(idToken);
                localStorage.setItem("token", idToken);
                setShowGoogleConnect(false);
                try {
                  const decodedToken = jwtDecode(idToken);
                  setUserEmail(decodedToken.email);
                  localStorage.setItem("userEmail", decodedToken.email);
                } catch (error) {
                  console.error("Error decoding token:", error);
                  handleLogout();
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Do not show iframe if no valid token
  return (
    <div className="App" style={appStyle}>
      <header className="loading-container">
        <div className="jantro-brand jantro-brand--hero">
          <div className="jantro-brand__mark-wrap">
            <IntegralMark className="jantro-brand__mark" />
            {showStageTab && (
              <span className="jantro-brand__stage-tab">
                <span className="jantro-brand__stage-dot" aria-hidden="true" />
                {stageTabLabel}
              </span>
            )}
          </div>
          <span className="jantro-brand__wordmark">Jantro</span>
        </div>
        loading...
        <div className="spinner"></div>
      </header>
    </div>
  );
}

export default App;
